<template>
  <div>
    <div class="d-flex justify-content-between">
      <h1 class="page-header">{{ $t('customerMemberCard.pageTitle') }}</h1>
      <ts-breadcrumb :items="[
        { text: $t('home'), href: '/' },
        {
          text: $t('customerMemberCard.pageTitle'),
          active: true
        }
      ]" />
    </div>
    <ts-card>
      <ts-card-wrapper>
        <div class="d-flex justify-content-between">
          <ts-button outline color="info" @click.prevent="addNew">{{
            $t('add_new')
          }}</ts-button>
          <div class="d-flex ts-space-x-3 ts-items-center">
            <a-select v-model="status_id" :options="statusList" option-filter-prop="children" class="ts-w-full"
              placeholder="Select"></a-select>
            <ts-input v-model="search" :placeholder="$t('search')" style="width: 250px" class="inline-block" />
          </div>
        </div>
      </ts-card-wrapper>
      <Table :columns="columns" :data="resources" size="small" :loading="loading" highlight-row stripe
        max-height="700">
        <template slot-scope="{ row }" slot="status">
          <span class="badge badge-success" v-if="row.status_id == 1">
            {{ row.status_name }}
          </span>
          <span class="badge badge-warning" v-else-if="row.status_id == 2">
            {{ row.status_name }}
          </span>
          <span class="badge badge-danger" v-else>
            {{ row.status_name }}
          </span>
        </template>
        <template slot-scope="{ row }" slot="activate_date">
          {{ row.activate_date }}
        </template>
        <template slot-scope="{ row }" slot="expiry_date">
          {{ row.expiry_date ? row.expiry_date : 'N/A' }}
        </template>
        <template slot-scope="{ row }" slot="promotion_type">
          <span class="badge badge-primary" v-if="row.promotion_type_id === 1">{{ $t('customerMemberCard.normal')
          }}</span>
          <span class="badge badge-info" v-else-if="row.promotion_type_id === 2">{{ $t('customerMemberCard.package')
          }}</span>
          <span v-else>-</span>
        </template>
        <template slot-scope="{ row }" slot="item">
          <span v-if="row.promotion_type_id === 1">{{ row.card_types || '-' }}</span>
          <span v-else-if="row.promotion_type_id === 2">{{ row.package_name || '-' }}</span>
          <span v-else>-</span>
        </template>
        <template slot-scope="{ row }" slot="action">
          <a href="#" v-tooltip="$t('pdf')" class="text-danger mr-2" @click.prevent="pdfView(row)"
            v-if="row.ref_stock_out_id">
            <i class="fas fa-file-pdf"></i>
          </a>
          <a href="#" v-tooltip="$t('renew')" class="text-primary mr-2" @click.prevent="renew(row)">
            <i class="fas fa-redo"></i>
          </a>
          <a href="#" v-tooltip="$t('edit')" class="text-primary mr-2" @click.prevent="edit(row)">
            <i class="fas fa-random"></i>
          </a>
          <a-popconfirm placement="leftTop" :title="$t('are_you_sure_to_delete')" :ok-text="$t('yes')"
            :cancel-text="$t('no')" @confirm="deleteRecord(row)">
            <a href="#" v-tooltip="$t('delete')" :disabled="row._deleting">
              <i class="fas fa-circle-notch spin" v-if="row._deleting"></i>
              <i class="fa fa-trash text-red" v-else></i>
            </a>
          </a-popconfirm>
        </template>
      </Table>
      <div class="d-flex justify-content-between align-items-center p-15">
        <ts-total-list v-model="pagination" />
        <ts-pagination v-model="pagination" @navigate="fetchData"></ts-pagination>
      </div>
      <!-- form-addnew -->
      <a-modal v-if="showForm" v-model="showForm" :title="$t('customerMemberCard.pageTitle')" centered
        @cancel="clearEdit" :zIndex="1040" :footer="null" width="800px">
        <from-action @cancel="clearEdit" @fetchData="fetchData" />
      </a-modal>
      <!-- form-update-status -->
      <a-modal v-if="showFormUpdate" v-model="showFormUpdate" :title="$t('customerMemberCard.pageTitle')" centered
        @cancel="clearEdit" :zIndex="1040" :footer="null" width="800px">
        <from-change-status @cancel="clearEdit" @fetchData="fetchData" />
      </a-modal>
      <!-- form-renew -->
      <a-modal v-if="showFormRenew" v-model="showFormRenew" :title="$t('customerMemberCard.pageTitle')" centered
        @cancel="clearEdit" :zIndex="1040" :footer="null" width="900px">
        <form-renew-card @cancel="clearEdit" @fetchData="fetchData" />
      </a-modal>
      <!-- pdf preview -->
      <pdf-preview v-model="pdf_view" :invoice-id="invoiceId" @cancel="value => (pdf_view = value)" />
    </ts-card>
  </div>
</template>

<script>
import { debounce } from 'lodash'
import { mapState } from 'vuex'
import FromAction from './form'
import FromChangeStatus from './form-change-status'
import FormRenewCard from './form-renew-card'
import PdfPreview from './pdf-preview'
import expandRow from './table-expand.vue'
export default {
  name: 'customerMemberCardIndex',
  components: {
    FromAction,
    FromChangeStatus,
    FormRenewCard,
    PdfPreview
  },
  data() {
    return {
      loading: false,
      showForm: false,
      showFormUpdate: false,
      showFormRenew: false,
      pdf_view: false,
      invoiceId: null,
      status_id: 1,
    }
  },
  created() {
    this.fetchData()
  },
  computed: {
    ...mapState('customer/customerMemberCard', ['resources', 'pagination']),
    search: {
      get() {
        return this.$store.state.customer.customerMemberCard.search
      },
      set(newValue) {
        this.$store.commit('customer/customerMemberCard/SET_SEARCH', newValue)
        this.$store.commit('customer/customerMemberCard/RESET_CURRENT_PAGE')
      }
    },
    columns() {
      return [
        {
          type: 'expand',
          render: (h, params) => {
            return h(expandRow, {
              props: {
                records: params.row.details
              },
              on: { cancel: this.fetchData }
            })
          },
          width: 30
        },
        {
          title: this.$t('customerMemberCard.customerName'),
          key: 'customer_name',
          width: 200
        },
        {
          title: this.$t('customerMemberCard.serialCard'),
          key: 'serial_card',
          width: 150
        },
        {
          title: this.$t('customerMemberCard.status'),
          slot: 'status',
          align: 'center',
          width: 150
        },
        {
          title: this.$t('customerMemberCard.gender'),
          key: 'gender',
          align: 'center',
          width: 100
        },
        {
          title: this.$t('customerMemberCard.contactNumber'),
          key: 'contact_number',
          align: 'center',
          width: 200
        },
        {
          title: this.$t('customerMemberCard.promotionType'),
          slot: 'promotion_type',
          align: 'center',
          width: 150
        },
        {
          title: this.$t('customerMemberCard.item'),
          slot: 'item',
          width: 180
        },
        {
          title: this.$t('customerMemberCard.maxTime'),
          key: 'max_times',
          align: 'center',
          width: 150
        },
        {
          title: this.$t('customerMemberCard.activateDate'),
          slot: 'activate_date',
          align: 'center',
          width: 200
        },
        {
          title: this.$t('customerMemberCard.expiryDate'),
          slot: 'expiry_date',
          align: 'center',
          width: 200
        },
        {
          title: this.$t('customerMemberCard.createdDate'),
          key: 'created_date',
          minWidth: 200
        },
        {
          title: this.$t('actions'),
          key: 'action',
          slot: 'action',
          fixed: 'right',
          align: 'center',
          className: 'ts-space-x-1',
          width: 140
        }
      ]
    },
    statusList() {
      return [
        { label: 'Active', value: 1 },
        { label: 'Inactive', value: 2 },
        { label: 'All', value: 3 }
      ]
    }
  },
  methods: {
    fetchData(attributes) {
      this.loading = true
      this.$store
        .dispatch('customer/customerMemberCard/fetch', { status_id: this.status_id, ...attributes })
        .then(response => {
          this.loading = false
        })
        .catch(error => {
          this.loading = false
          this.$toasted.error(error.message)
        })
    },
    addNew() {
      this.showForm = true
    },
    renew(record) {
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', record)
      this.showForm = true
    },
    edit(record) {
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', record)
      this.showForm = true
    },
    clearEdit() {
      this.$store.commit('customer/customerMemberCard/SET_EDIT_DATA', {})
      this.showForm = false
      this.showFormUpdate = false
      this.showFormRenew = false
      this.fetchData()
    },
    pdfView(record) {
      this.invoiceId = record.ref_stock_out_id
      this.pdf_view = true
    },
    deleteRecord(record) {
      record._deleting = true
      this.$store
        .dispatch(
          'customer/customerMemberCard/destroy',
          record.customer_member_card_id
        )
        .then(response => {
          this.$toasted.success(response.message)
          this.fetchData()
        })
        .catch(error => {
          this.$toasted.error(error.message)
        })
        .finally(() => {
          record._deleting = false
        })
    },
    branchFilter(value) {
      this.$store.commit('customer/customerMemberCard/SET_BRANCH_ID', value)
      this.fetchData()
    }
  },
  watch: {
    search: debounce(function (search) {
      this.fetchData()
    }, 500),
    status_id: debounce(function () {
      this.fetchData()
    }, 500),
  },
  beforeRouteLeave(to, from, next) {
    this.$store.commit('customer/customerMemberCard/RESET_STATE')
    next()
  }
}
</script>
